import type { Dictionary } from './en';

/**
 * Kazakh.
 *
 * Two plural forms — `one` and `other` — and the noun after a number stays
 * singular, so "5 рейсов" has no Kazakh equivalent: it is "5 рейс". `Intl` knows
 * this for `kk`; the dictionary only has to supply the forms the language uses.
 */
export const kk: Dictionary = {
  app: {
    built: 'Жиналды {date}',
    name: 'Flightplan',
    tagline: 'Операциялық бақылау',
  },

  theme: {
    light: 'Жарық тақырып',
    dark: 'Қараңғы тақырып',
  },

  language: {
    label: 'Тіл',
  },

  auth: {
    username: 'Логин',
    password: 'Құпия сөз',
    signIn: 'Кіру',
    signingIn: 'Кіру…',
    signOut: 'Шығу',
    showPassword: 'Құпия сөзді көрсету',
    hidePassword: 'Құпия сөзді жасыру',
    invalidCredentials: 'Логин немесе құпия сөз дұрыс емес, немесе тіркелгі бұғатталған.',
    networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
    serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
    unexpectedResponse: 'Сервер қолданба танымайтын пішімде жауап берді.',
    sessionExpired: 'Сеанс бітті. Қайта кіріңіз.',
    notPermitted: 'Бұл бөлім сіздің рөлге қолжетімді емес.',
    role: 'Рөл',
    noRole: 'Рөлсіз',
  },
  nav: {
    styleGuide: 'Дизайн жүйесі',
    onlineTable: 'Ұшу кестесі',
    flightMap: 'Рейстер картасы',

    internationalArrivals: 'Халықаралық қонулар',
    airports: 'Әуежайлар',
    airlines: 'Әуе компаниялары',
    integrationMonitor: 'Интеграция мониторы',
    userManagement: 'Пайдаланушылар',
    administration: 'Әкімшілік',
    profile: 'Сіздің тіркелгіңіз',
    elkDashboard: 'ELK дашборды',
    openMenu: 'Мәзірді ашу',
    closeMenu: 'Мәзірді жабу',
  },

  profile: {
    title: 'Сіздің тіркелгіңіз',
    lead: 'Атыңыз, сондай-ақ құпия сөзді ауыстыру. Мұны тек сіз өзгерте аласыз — басқалардың жазбасын әкімші «Пайдаланушылар» бөлімінде түзетеді.',
    account: 'Тіркелгі',
    adminOnlyNote:
      'Логин мен рөлді әкімші белгілейді — оларды мұнда өзгерту мүмкін емес.',
    changePassword: 'Құпия сөзді ауыстыру',
    currentPassword: 'Ағымдағы құпия сөз',
    newPassword: 'Жаңа құпия сөз',
    confirmPassword: 'Жаңа құпия сөзді қайталаңыз',
    passwordMismatch: 'Құпия сөздер сәйкес келмейді.',
    saved: 'Тіркелгі жаңартылды.',
    nothingChanged: 'Ештеңе өзгермеді, сұраныс жіберілмеді.',
    saveFailed: 'Сервер өзгерісті қабылдамады, бірақ қай өріс қате екенін айтпады.',
    networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
    serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
    errorLoad: 'Тіркелгіңізді жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    flightAccess: 'Рейстерге қолжетімділік',
    flightAccessSees: 'Сіз көресіз',
    flightAccessAll: 'Сіз барлық рейстерді көресіз.',
    flightAccessAdmin: 'Сіз барлық рейстерді көресіз: әкімшінің қолжетімділігі шектелмейді.',
    flightAccessRestricted: 'Сіз тек осы рейстерді көресіз. Қолжетімділікті әкімші белгілейді — көбірек керек болса, әкімшіге хабарласыңыз.',
    flightAccessFailed: 'Рейстерге қолжетімділігіңізді жүктеу мүмкін болмады.',
  },

  flightAccess: {
    all: 'Барлық рейстер',
    airportsOnly: '{airports} әуежайына ұшатын немесе одан ұшып шығатын барлық рейстер',
    airlinesOnly: '{airlines} әуе компаниясының барлық рейстері',
    both: '{airlines} әуе компаниясының {airports} әуежайына ұшатын немесе одан ұшып шығатын рейстері',
    incomplete: 'Бірде-бір рейс жоқ — кемінде бір әуежайды немесе бір әуе компаниясын таңдаңыз',
    or: 'немесе',
    yours: 'Сіздің рейстеріңіз',
    yoursHint: 'Сіз тек осы рейстерді көресіз — қалғандарын сервер көрсетпейді. Қолжетімділікті әкімші белгілейді; көбірек керек болса, әкімшіге хабарласыңыз.',
    emptyHint: 'Сіз тек жоғарыда көрсетілген қолжетімділіктегі рейстерді көресіз. Одан тыс сүзгі ештеңе көрсетпейді.',
    allYourStations: 'Барлық әуежайларыңыз',
  },

  airlines: {
    title: 'Әуе компаниялары',
    add: 'Әуе компаниясын қосу',
    found: 'Әуе компаниялары',
    searchPlaceholder: 'Код, префикс немесе атауы',
    allStates: 'Барлық тасымалдаушы',
    resetFilters: 'Барлығын тазалау',
    sortHint: 'Осы баған бойынша сұрыптау',
    nothingFound: 'Бұл сүзгілерге сәйкес әуе компаниясы жоқ',
    notFound: 'Бұл әуе компаниясы анықтамада жоқ — ескі сілтеме арқылы ашылған немесе жойылған болуы мүмкін.',
    errorLoad: 'Анықтаманы жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Әуе компаниялары анықтамасы сіздің рөлге қолжетімді емес.',
    states: {
      working: 'Жұмыс тізімінде',
      notWorking: 'Тізімнен тыс',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Әуе компаниясы',
      prefix: 'Префикс',
      working: 'Жұмыс тізімі',
    },
    flash: {
      created: '{code} әуе компаниясы құрылды.',
      saved: '{code} өзгерістері сақталды.',
      deleted: '{code} әуе компаниясы жойылды.',
    },
    panel: {
      create: 'Жаңа әуе компаниясы',
      createAction: 'Әуе компаниясын құру',
      edit: 'Әуе компаниясын өзгерту',
      noCode: 'Кодсыз',
      readOnly: 'Тек қарау. Әуе компаниялары анықтамасын тек әкімші өзгерте алады.',
      codes: 'Кодтар',
      names: 'Атаулары',
      details: 'Күйі және ескертпелер',
      iataLength: 'Дәл {count} таңба.',
      icaoLength: 'Дәл {count} таңба немесе бос.',
      title: 'Қысқаша атауы',
      fullTitle: 'Толық атауы',
      description: 'Ескертпелер',
      saving: 'Сақталуда…',
      saveFailed: 'Сервер өзгерісті себебін айтпай қабылдамады.',
      forbidden: 'Әуе компаниялары анықтамасын тек әкімші өзгерте алады.',
      networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
      serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
      deleteTitle: 'Әуе компаниясын жою',
      deleteHint: 'Жазба біржола жойылады, мұнда белсенділік белгісі жоқ. Тасымалдаушыны жоймай, жұмыс тізімінен шығару үшін жоғарыдан «Тізімнен тыс» таңдаңыз.',
      deleteConfirm: '{code} біржола жойылсын ба? Артынан ештеңе тазаланбайды — кестеде тасымалдаушы коды жай мәтін ретінде сақталады, сондықтан орындалған барлық рейсте артында ештеңесі жоқ код қалады.',
      deleteConfirmAction: 'Иә, жою',
      deleteFailed: 'Әуе компаниясы жойылмады.',
    },
  },

  internationalArrivals: {
    title: 'Халықаралық қонулар',
    errorLoad: 'Журналды жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Халықаралық қонулар журналы сіздің рөлге қолжетімді емес.',
    resetFilters: 'Барлығын тазалау',
    sortHint: 'Осы баған бойынша сұрыптау',
    allStations: 'Барлық әуежайлар',
    nothingFound: 'Бұл сүзгілерге сәйкес қону жоқ',
    rowsFound: 'Қонулар',
    staFrom: 'Кесте бойынша бастап',
    staTo: 'Кесте бойынша дейін',
    state: {
      label: 'Жолдар',
      active: 'Тек белсенді',
      withdrawn: 'Тек алынған',
      all: 'Белсенді және алынған',
    },
    columns: {
      airportIataCode: 'Әуежай',
      flt: 'Рейс',
      orgn: 'Ұшу әуежайы',
      sta: 'Кесте бойынша күні',
      ata: 'Нақты күні',
      br: 'ӘУ тұрағына келуі',
      firstPax: 'Бірінші жолаушы',
      lastPax: 'Соңғы жолаушы',
      firstBag: 'Бірінші жүк',
      lastBag: 'Соңғы жүк',
      ttlPax: 'Жолаушылар жиыны',
      ttlBag: 'Жүк жиыны',
      spot: 'Тұрақ',
      acType: 'ӘК түрі',
    },
  },
  airports: {
    title: 'Әуежайлар',
    add: 'Әуежай қосу',
    found: 'Әуежайлар',
    searchPlaceholder: 'Код, әуежай, қала немесе ел',
    countryPlaceholder: 'Код немесе атауы',
    allStates: 'Кез келген күй',
    resetFilters: 'Барлығын тазалау',
    sortHint: 'Осы баған бойынша сұрыптау',
    nothingFound: 'Бұл сүзгілерге сәйкес әуежай жоқ',
    notFound: 'Бұл әуежай ағымдағы бетте жоқ — ескі сілтеме арқылы ашылған немесе жойылған болуы мүмкін.',
    errorLoad: 'Анықтаманы жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Әуежайлар анықтамасы сіздің рөлге қолжетімді емес.',
    errorSort: 'Сервер бұл баған бойынша сұрыптамайды. Басқасын таңдаңыз.',
    states: {
      active: 'Белсенді',
      inactive: 'Белсенсіз',
    },
    columns: {
      iata: 'IATA',
      icao: 'ICAO',
      name: 'Әуежай',
      city: 'Қала',
      country: 'Ел',
      timeZone: 'Уақыт белдеуі',
      status: 'Күйі',
      created: 'Құрылды',
      updated: 'Өзгертілді',
    },
    flash: {
      created: '{code} әуежайы құрылды.',
      saved: '{code} өзгерістері сақталды.',
      deleted: '{code} әуежайы жойылды.',
    },
    panel: {
      create: 'Жаңа әуежай',
      createAction: 'Әуежай құру',
      edit: 'Әуежайды өзгерту',
      noCode: 'Кодсыз',
      readOnly: 'Тек қарау. Әуежайлар анықтамасын тек әкімші өзгерте алады.',
      codes: 'Кодтар',
      airportName: 'Әуежай атауы',
      place: 'Орналасуы',
      inEnglish: 'Ағылшынша',
      inRussian: 'Орысша',
      inKazakh: 'Қазақша',
      countryCode: 'Ел коды',
      timeZoneSearch: 'Іздеу: Almaty, +5',
      timeZoneNone: 'Сәйкес уақыт белдеуі жоқ',
      timeZoneClear: 'Уақыт белдеуін тазарту',
      latitude: 'Ендік',
      longitude: 'Бойлық',
      saving: 'Сақталуда…',
      missingRequired: 'Төменде белгіленген өрістерді толтырыңыз.',
      saveFailed: 'Сервер өзгерісті себебін айтпай қабылдамады.',
      forbidden: 'Әуежайлар анықтамасын тек әкімші өзгерте алады.',
      networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
      serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
      deleteTitle: 'Әуежайды жою',
      deleteHint: 'Жазба біржола жойылады. Әуежайды жоймай, ұсынуды тоқтату үшін жоғарыдан «Ұсынылмайды» күйін қойыңыз.',
      deleteConfirm: '{code} біржола жойылсын ба? Бұл әрекетті қайтару мүмкін емес, ал осы кодпен сәйкестендірілген барлық рейстердің артында ештеңе қалмайды.',
      deleteConfirmAction: 'Иә, жою',
      deleteFailed: 'Әуежай жойылмады.',
    },
  },

  users: {
    title: 'Пайдаланушылар',
    add: 'Пайдаланушы қосу',
    // Kazakh keeps the noun singular after a number, so both forms are the same
    // string — a wrong plural selection then still reads correctly.
    found: { one: '{count} пайдаланушы', other: '{count} пайдаланушы' },
    ofTotal: '{total} ішінен',
    searchPlaceholder: 'Логин немесе аты',
    allRoles: 'Барлық рөл',
    allStates: 'Кез келген күй',
    resetFilters: 'Барлығын тазалау',
    sortHint: 'Осы баған бойынша сұрыптау',
    nothingFound: 'Бұл сүзгілерге сәйкес пайдаланушы жоқ',
    notFound: 'Мұндай пайдаланушы тізімде жоқ — ескі сілтеме арқылы ашылған болуы мүмкін.',
    errorLoad: 'Пайдаланушыларды жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Пайдаланушылар тізімі тек әкімшіге қолжетімді.',
    allAccess: 'Рейстерге кез келген қолжетімділік',
    states: {
      active: 'Белсенді',
      blocked: 'Бұғатталған',
    },
    accessStates: {
      all: 'Барлық рейстер',
      restricted: 'Шектелген',
    },
    roles: {
      ADMIN: 'Әкімші',
      AAK: 'ААК',
      USER: 'Пайдаланушы',
      KGA_USER: 'АКК пайдаланушысы',
      INTEGRATOR: 'Интегратор',
      OPERATOR: 'Оператор',
      DISPLAY: 'Экран',
    },
    columns: {
      username: 'Логин',
      name: 'Аты',
      role: 'Рөл',
      access: 'Рейстерге қолжетімділік',
      status: 'Күйі',
      created: 'Құрылды',
    },
    flash: {
      created: '{name} пайдаланушысы құрылды.',
      saved: '{name} өзгерістері сақталды.',
      blocked: '{name} енді кіре алмайды.',
      unblocked: '{name} қайта кіре алады.',
      savedAndSignedOut: '{name} өзгерістері сақталды. {name} барлық құрылғыларда жүйеден шығарылды.',
      sessionsEnded: '{name} барлық құрылғыларда жүйеден шығарылды.',
      createdWithoutAccess: '{name} пайдаланушысы құрылды, бірақ рейстерге қолжетімділік сақталмады. Ол сақталғанша, {name} барлық рейстерді көреді.',
    },
    access: {
      title: 'Рейстерге қолжетімділік',
      all: 'Барлық рейстер',
      allHint: 'Барлық рейстерді көреді.',
      selected: 'Тек таңдалған рейстер',
      selectedHint: 'Тек төмендегі әуежайлар мен әуе компанияларына сәйкес келетін рейстерді көреді.',
      airports: 'Әуежайлар',
      airportsHint: 'Рейс солардың бірінен ұшады немесе біріне қонады. Бос — кез келген әуежай.',
      airlines: 'Әуе компаниялары',
      airlinesHint: 'Рейсті солардың бірі орындайды. Бос — кез келген әуе компаниясы.',
      airportSearch: 'Код немесе қала',
      airlineSearch: 'Код немесе атауы',
      remove: '{code} алып тастау',
      offList: 'жұмыс тізімінде жоқ',
      sees: 'Көреді',
      needOne: 'Кемінде бір әуежайды немесе бір әуе компаниясын таңдаңыз.',
      save: 'Қолжетімділікті сақтау',
      saved: 'Рейстерге қолжетімділік сақталды.',
      nothingChanged: 'Қолжетімділік өзгермеді, сұраныс жіберілмеді.',
      saveFailed: 'Сервер қолжетімділікті себебін айтпай қабылдамады.',
      changed: '{date} өзгертілді, {name}',
      neverSet: 'Белгіленбеген: әдепкі бойынша — барлық рейстер.',
      adminNote: 'Әкімші әрқашан барлық рейстерді көреді, оның қолжетімділігін шектеу мүмкін емес.',
      adminStored: 'Бұл тіркелгі үшін шектеу сақталған — {description}. Рөл «Әкімші» болғанда ол әрекет етпейді; рөл алынса, қайта күшіне енеді.',
      clearStored: 'Сақталған шектеуді жою',
      cleared: 'Сақталған шектеу жойылды.',
      adminIgnored: 'шектеу әрекет етпейді — әкімші',
      errorLoad: 'Рейстерге қолжетімділікті жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
      listFailed: 'Пайдаланушылардың рейстерге қолжетімділігін жүктеу мүмкін болмады, сондықтан баған бос және қолжетімділікті өзгерту мүмкін емес. Қайталау үшін жаңартыңыз.',
      retry: 'Қайталау',
      createNote: 'Пайдаланушы құрылғаннан кейін бірден сақталады — құпия сөзді бергенге дейін.',
    },
    panel: {
      create: 'Жаңа пайдаланушы',
      createAction: 'Пайдаланушы құру',
      edit: 'Пайдаланушыны өзгерту',
      details: 'Деректер',
      you: 'Сіз',
      account: 'Тіркелгі',
      username: 'Логин',
      name: 'Аты',
      nameHint: 'Адамның аты-жөні немесе тіркелгінің атауы — мысалы, «ALA диспетчерлік бөлмесі».',
      password: 'Құпия сөз',
      passwordHint: 'Кемінде {count} таңба.',
      passwordTooShort: 'Кемінде {count} таңба.',
      newPassword: 'Құпия сөзді қалпына келтіру',
      role: 'Рөл',
      roleSelf: 'Өз рөліңізді өзгерте алмайсыз — мұны басқа әкімші жасауы керек.',
      noRole: 'Рөлсіз',
      created: 'Құрылды',
      updated: 'Өзгертілді',
      saving: 'Сақталуда…',
      nothingChanged: 'Ештеңе өзгермеді, сұраныс жіберілмеді.',
      saveFailed: 'Сервер өзгерісті қабылдамады, бірақ қай өріс қате екенін айтпады.',
      networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
      serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
      displayHint: 'Қабырға экраны: тек рейстер картасын көрсетеді. Жүйеде 30 күн қалады және кіруді күн сайын ұзартады.',
      endSessionsTitle: 'Барлық құрылғыларда шығу',
      endSessionsHint: 'Осы тіркелгімен кірген барлық құрылғылар жүйеден шығарылады. Тіркелгі өзгермейді және бірден қайта кіре алады — экранның кіруі көшірілген болуы мүмкін болса, осыны пайдаланыңыз.',
      endSessionsSelf: 'Сіз кірген барлық құрылғылар, осы құрылғыны қоса, жүйеден шығарылады.',
      endSessions: 'Барлық құрылғыларда шығу',
      blockTitle: 'Тіркелгіні бұғаттау',
      blockHint: 'Пайдаланушы кіре алмайды. Ештеңе жойылмайды.',
      block: 'Бұғаттау',
      blockSelf: 'Өз тіркелгіңізді бұғаттай алмайсыз.',
      unblockTitle: 'Тіркелгі бұғатталған',
      unblockHint: 'Қолжетімділік қалпына келтірілгенше пайдаланушы кіре алмайды.',
      unblock: 'Қолжетімділікті қалпына келтіру',
    },
  },

  monitor: {
    title: 'Интеграция мониторы',
    views: {
      sources: 'Дереккөздер',
      events: 'Оқиғалар',
      messages: 'Қабылданған хабарламалар',
    },
    errorLoad: 'Мониторды жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Интеграция мониторы тек әкімшіге қолжетімді.',
    errorFilter: 'Сервер сүзгіні қабылдамады — сақталған сілтеме енді жоқ дереккөзді атауы мүмкін. Сүзгілерді тазалап, қайталаңыз.',
    clearFilters: 'Барлығын тазалау',
    fields: {
      scheduledDate: 'Кесте бойынша уақыт',
      estimatedDate: 'Болжамды уақыт',
      actualDate: 'Нақты уақыт',
      departedTime: 'Шығу пунктінен ұшуы',
      flightStatus: 'Күйі',
      delayMinutes: 'Кідіріс, мин',
      delayReason: 'Кідіріс себебі',
      passengerAmount: 'Жолаушылар',
      totalCargoWeight: 'Жүк, кг',
      planeParkingName: 'Тұрақ',
      externalFlightId: 'Сыртқы идентификатор',
      active: 'Кестеде',
    },
    changes: {
      cleared: 'тазаланды',
    },
    payloadGone: 'Жазба сақтау мерзімі бойынша жойылған.',
    payloadEmpty: 'Бұл жазба үшін ештеңе сақталмаған.',
    deliveryNoContent:
      'Бұл жеткізілімнің мазмұны сақталмайды. Сәтті кесте суреті файлды әдейі сақтамайды — әр рейс бойынша жазба оқиғаның өзінде.',
    payloadFailed: 'Жүктеу мүмкін болмады.',
    health: {
      OK: 'Дұрыс',
      STALE: 'Жауап жоқ',
      FAILING: 'Ақау',
      UNKNOWN: 'Деректер жоқ',
    },
    sources: {
      lead: 'Қолданба іске қоса алатын барлық дереккөз — бірдеңе жеткізді ме, жоқ па. Күй дереккөздің қолжетімділігі бойынша бағаланады: айтары жоқ арна жұмыс істейді, қол жетпейтін арна — жоқ.',
      columns: {
        source: 'Дереккөз',
        health: 'Күйі',
        interval: 'Интервал',
        trust: 'Санат',
        updated: 'Баптаулар өзгертілді',
      },
      off: 'Өшірулі',
      push: 'Push',
      defaults: 'Әдепкі',
      seconds: '{count} с',
      minutes: '{count} мин',
      replaying: 'Архивті қайта оқу: {progress}',
      replayingStart: 'Архивті қайта оқу…',
    },
    panel: {
      title: 'Дереккөз баптаулары',
      displayName: 'Көрсетілетін атауы',
      enabled: 'Қосулы',
      on: 'Қосулы',
      interval: 'Күтілетін аралық, секунд',
      intervalInvalid: 'Бүтін сан (секунд) немесе бос.',
      resetDefault: 'әдепкі {value}',
      trust: 'Сенім рангі',
      trustInvalid: '0 мен 100 арасындағы бүтін сан.',
      updatedBy: 'Өзгерткен: {name}, {date}',
      saved: 'Сақталды.',
      saving: 'Сақталуда…',
      showErrors: 'Соңғы қателер',
      replayTitle: 'Бүкіл архивті қайта оқу',
      replayHint: 'Барлық архив файлын бүгінгі талдаудан өткізеді. Тек олқылықтарды толтырады — кестеде бар нәрсе қайта жазылмайды. Әр сұрауда бес файл, сондықтан бұл тез емес, ал деплой процесті тоқтатады.',
      replayStart: 'Қайта оқуды бастау',
      replayCancel: 'Қайта оқуды тоқтату',
      replayRunning: 'Жүріп жатыр — соңғы аяқталған файл: {progress}',
      replayRunningStart: 'Жүріп жатыр — әзірге бірде-бір файл аяқталған жоқ',
      networkError: 'Сервер қолжетімсіз. Қосылымды тексеріп, қайталап көріңіз.',
      serverError: 'Сервер сұранысты өңдей алмады. Сәл кейін қайталаңыз.',
    },
    events: {
      found: 'Оқиғалар',
      columns: {
        time: 'Уақыты',
        type: 'Оқиға',
        flight: 'Рейс',
        details: 'Өзгерістер',
        payload: 'Хабарлама',
      },
      types: {
        CREATED: 'Құрылды',
        UPDATED: 'Өзгертілді',
        DELETED: 'Жойылды',
        RECEIVED: 'Қабылданды',
        WARNING: 'Ескерту',
        ERROR: 'Қате',
      },
      allSources: 'Барлық дереккөз',
      allTypes: 'Барлық оқиға',
      flightLabel: 'Рейс нөмірі',
      flightExample: 'KC222',
      rowLabel: 'Кесте ID',
      rowExample: '395239',
      show: 'Көрсету',
      hide: 'Жасыру',
      allLegs: 'Екі бағыт та',
      fieldLabel: 'Өзгерген өріс',
      allFields: 'Кез келген өріс',
      periodLabel: 'Кезең',
      defaultWindow: 'Соңғы 48 сағат',
      lastDays: 'Соңғы {count} күн',
      sourceRecord: 'Дереккөз осы рейс бойынша не жіберді',
      wholeDelivery: 'Хабарлама толығымен',
      window: 'Соңғы 48 сағат көрсетілген. Әрі қарай қарау үшін рейс нөмірі немесе кесте ID бойынша сүзіңіз.',
      fullHistory: 'Осы рейстің бүкіл тарихы көрсетілген — 48 сағатпен шектелмейді.',
      payload: 'Хабарлама',
      noSource: 'дереккөзсіз',
      nothingFound: 'Бұл сүзгілерге сәйкес оқиға жоқ',
    },
    messages: {
      found: 'Хабарламалар',
      lead: 'Дереккөздер жіберген барлық нәрсе, бастапқы мазмұнымен бірге — телекс, FTP файлы, XML сұрауы, CSV. Мазмұны өзгермеген сұрау сақталмайды, сондықтан тыныш арна — сау арна: тірілік «Дереккөздердегі» күй индикаторынан көрінеді, мұндағы жол санынан емес.',
      columns: {
        kind: 'Түрі',
        status: 'Статус',
        rows: 'Өзгертті / келді',
        ref: 'Сілтеме',
      },
      statuses: {
        PROCESSED: 'Өңделді',
        UNCHANGED: 'Өзгеріссіз',
        FAILED: 'Сәтсіз',
      },
      nothingFound: 'Бұл сүзгілерге сәйкес хабарлама жоқ',
    },
  },

  map: {
    title: 'Әуедегі рейстер',
    mapLabel: 'Әуедегі рейстер картасы',
    showKazakhstan: 'Қазақстан',
    basemapFailed: 'Карта контурларын жүктеу мүмкін болмады. Рейстер дұрыс орналастырылған.',
    inAir: { one: 'Әуеде {count} рейс', other: 'Әуеде {count} рейс' },
    updatedAt: '{time} жаңартылды',
    showingFrom: '{time} кездегі деректер көрсетілген.',
    errorLoad: 'Рейстерді жаңарту мүмкін болмады. Қосылымды тексеріңіз — карта әр минут сайын қайталайды.',
    errorForbidden: 'Ұшу кестесі сіздің рөлге қолжетімді емес.',
    approximate:
      'Орны рейстің ұшып шыққан уақыты мен күтілетін келу уақыты бойынша есептелген — кесте әуе кемесінің қай жерде екенін хабарламайды.',
    withoutCoordinates: 'Картада жоқ — әуежайлар анықтамасында координаттар жоқ: {codes}.',
    selectedGone: 'Осы сілтемедегі рейс енді әуеде емес.',
    empty: 'Қазір әуеде рейстер жоқ.',
    minutes: '{minutes} мин',
    hoursMinutes: '{hours} сағ {minutes} мин',
    inDuration: '{duration} кейін',
    overdueBy: 'күтілгеннен {duration} кеш',
    backToList: 'Тізімге оралу',
    percentFlown: 'Жолдың {percent}%',
    departed: 'Ұшып шықты',
    expected: 'Күтілуде',
    remaining: 'Қалды',
    overdue: 'Күтілгеннен кеш',
    awaitingLanding: 'Қонуы әлі хабарланбаған.',
    landed: 'Қонды',
    landedAgo: '{duration} бұрын қонды',
    landedJustNow: 'жаңа ғана қонды',
    landedCount: { one: '{count} қонды', other: '{count} қонды' },
    basis: {
      LANDED: 'хабарланған қону уақыты',
      ESTIMATED: 'дереккөз хабарлаған есептік келу уақыты',
      SCHEDULED: 'жоспарлы ұшу уақыты бойынша — есептік уақыт хабарланбаған',
      DISTANCE: 'қашықтық бойынша — келу уақыты хабарланбаған',
    },
    distance: 'Қашықтық',
    km: '{distance} км',
    registration: 'Борт нөмірі',
    openInSchedule: 'Кестеде ашу',
  },

  common: {
    date: 'Күні',
    time: 'Уақыты',
    save: 'Сақтау',
    cancel: 'Болдырмау',
    close: 'Жабу',
    delete: 'Жою',
    search: 'Іздеу',
    back: 'Артқа',
    refresh: 'Жаңарту',
    loading: 'Жүктелуде…',
    noData: 'Деректер жоқ',
    required: 'Толтыру қажет',
    yes: 'Иә',
    no: 'Жоқ',
  },

  schedule: {
    title: 'Ұшу кестесі',
    previousPeriod: 'Алдыңғы кезең',
    nextPeriod: 'Келесі кезең',
    scheduledFrom: 'Кесте бойынша, бастап',
    scheduledTo: 'Кесте бойынша, дейін',
    range: {
      label: 'Кезең',
      today: 'Бүгін',
      yesterday: 'Кеше',
      tomorrow: 'Ертең',
      last24h: 'Соңғы 24 сағат',
      last7d: 'Соңғы 7 күн',
      next7d: 'Келесі 7 күн',
      last30d: 'Соңғы 30 күн',
      thisMonth: 'Осы ай',
      custom: 'Өз кезеңі',
      invalid: 'Кезеңнің соңы басталуынан бұрын.',
    },
    allTypes: 'Бәрі',
    allStations: 'Барлық әуежайлар',
    export: {
      label: 'Жүктеп алу',
      excel: 'Excel (.xlsx)',
      apcas: 'APCAS (.csv)',
      working: 'Дайындалуда…',
      error: 'Файлды жүктеп алу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    },
    resetFilters: 'Барлығын тазалау',
    clearFilter: 'Тазалау: {name}',
    noMatches: 'Әуе компаниясы табылмады',
    airlinesFailed: 'Әуе компаниялар тізімі жүктелмеді — кодты қолмен енгізуге болады',
    moreMatches: 'Тағы {count} — тізімді тарылту үшін теруді жалғастырыңыз',
    airportsFailed: 'Әуежай іздеу сәтсіз болды — кодты қолмен енгізуге болады',
    searching: 'Іздеу…',
    laterBy: 'Кестеден {count} мин кеш',
    earlierBy: 'Кестеден {count} мин ерте',
    nothingFound: 'Осы сүзгілерге сай рейс жоқ',
    statusLegend: 'Мәртебе түстері',
    firstPage: 'Бірінші бет',
    previousPage: 'Алдыңғы бет',
    nextPage: 'Келесі бет',
    lastPage: 'Соңғы бет',
    sortHint: 'Осы баған бойынша сұрыптау',
    perPage: 'Бетте',
    pageOf: '{pages} беттен {page}-беті',
    pageOnly: '{page}-бет',
    rowsFound: 'Рейстер',
    bothLegsHint: 'Әрбір рейс — екі жол, әр иінге бір, сондықтан рейс нөмірі бойынша қону да, ұшу да қайтарылады.',
    errorLoad: 'Кестені жүктеу мүмкін болмады. Қосылымды тексеріп, қайталаңыз.',
    errorForbidden: 'Сіздің рөліңізге ұшу кестесі қолжетімді емес.',
    scopes: {
      DOMESTIC: 'Ішкі',
      INTERNATIONAL: 'Халықаралық',
    },
    detail: {
      title: 'Рейс картасы',
      thisStation: 'осы аэропорт',
      routeAlt: 'Бағыт {from} — {to}',
      withdrawn: 'Алынды',
      notFound: 'Бұл рейс кестеде жоқ.',
      otherLeg: 'Екінші рейсті ашу — {station} жеріндегі {leg}',
      history: 'Өзгерістер тарихы',
      noHistory: 'Рейс құрылғаннан бері өзгеріс болмады.',
      historyFailed: 'Тарихты жүктеу мүмкін болмады. Рейстің өзі жоғарыда көрсетілген.',
      archiveNote: 'AIRPORT дереккөзінен алынған жазбалар оқиғаларға көшкенге дейінгі мұрағаттан келеді: олар сол кездегі мәнді сақтайды, өзгерістің өзін емес, және хабарламасы жоқ.',
      archivedRow: 'Мұрағаттық жазба: өзгерістер оқиға ретінде тіркелместен бұрын жазылған.',
      unknownSource: 'дереккөз белгісіз',
      events: {
        CREATED: 'Құрылды',
        UPDATED: 'Жаңартылды',
        DELETED: 'Жойылды',
      },
      showRaw: 'Хабарлама',
      hideRaw: 'Жасыру',
      delayed: 'Кешігу {count} мин',
      departedFrom: 'Шығу пунктінен ұшуы',
      passengers: 'Жолаушылар',
      cargo: 'Жүк',
      kg: 'кг',
      times: { scheduled: 'Кесте бойынша', estimated: 'Есептік', actual: 'Нақты' },
      errorLoad: 'Рейсті жүктеу мүмкін болмады. Қайталап көріңіз.',
      errorForbidden: 'Сіздің рөліңізге бұл рейс қолжетімді емес.',
    },
    columns: {
      flight: 'Рейс нөмірі',
      airline: 'Әуе компаниясы',
      station: 'Әуежай',
      leg: 'Бағыт',
      origin: 'Ұшу әуежайы',
      destination: 'Қону әуежайы',
      scheduled: 'Кесте бойынша',
      estimated: 'Болжамды',
      actual: 'Нақты',
      status: 'Мәртебесі',
      stand: 'Тұрақ',
      delayReason: 'Кешігу коды',
      passengers: 'Жолаушылар саны',
    },
    types: {
      ARRIVAL: 'Қону',
      DEPARTURE: 'Ұшу',
    },
    statuses: {
      EXPECTED: 'Күтіледі',
      ON_TIME: 'Кесте бойынша',
      DELAYED: 'Кешігуде',
      CHECK_IN: 'Тіркеу',
      BOARDING: 'Отырғызу',
      DEPARTED_EARLY: 'Ерте ұшты',
      DEPARTED_ON_TIME: 'Кесте бойынша ұшты',
      DEPARTED_LATE: 'Кешігіп ұшты',
      LANDED_EARLY: 'Ерте қонды',
      LANDED_ON_TIME: 'Кесте бойынша қонды',
      LANDED_LATE: 'Кешігіп қонды',
      CANCELLED: 'Болдырылмады',
      UNKNOWN: 'Белгісіз',
    },
  },

  flight: {
    number: 'Рейс',
    route: 'Бағыт',
    scheduled: 'Кесте бойынша',
    estimated: 'Болжамды',
    actual: 'Нақты',
    status: 'Мәртебесі',
    found: {
      one: '{count} рейс',
      other: '{count} рейс',
    },
    delayedBy: {
      one: '{count} минутқа кешігуде',
      other: '{count} минутқа кешігуде',
    },
  },

  // Kept here rather than taken from Intl: Chrome has no month names for
  // Kazakh and silently formats it as "2026 M07 24".
  months: {
    jan: 'қаңтар',
    feb: 'ақпан',
    mar: 'наурыз',
    apr: 'сәуір',
    may: 'мамыр',
    jun: 'маусым',
    jul: 'шілде',
    aug: 'тамыз',
    sep: 'қыркүйек',
    oct: 'қазан',
    nov: 'қараша',
    dec: 'желтоқсан',
  },

  status: {
    onTime: 'Кесте бойынша',
    delayed: 'Кешігуде',
    departed: 'Ұшып кетті',
    landed: 'Қонды',
    cancelled: 'Болдырылмады',
    expected: 'Күтіледі',
  },

  demo: {
    signedInAs: '{name} ретінде кірдіңіз',
    lastUpdated: '{time} жаңартылды',
    cargo: '{count} рейсте {weight} кг жүк',
  },
};
